import React, {useEffect, useState} from 'react';
import {Button, Col, InputNumber, Row, Slider, Typography} from 'antd';
import {AimOutlined} from '@ant-design/icons';
import GeoRadiusMap, {GeoPoint} from '../../../molecules/Forms/GeoRadiusMap/component';
import styles from './style.module.css';
import {defaultRadiusKm, GeoRadiusValue} from '../../../../utils/campaignGeo';

const {Text} = Typography;

type GeoRadiusPickerProps = {
    value?: GeoRadiusValue | null;
    onChange?: (value: GeoRadiusValue) => void;
};

const defaultCenter: GeoPoint = {lat: 40.7128, lng: -74.006};
const minRadiusKm = 1;
const maxRadiusKm = 500;

const isValidGeoValue = (value?: GeoRadiusValue | null): value is GeoRadiusValue =>
    typeof value?.lat === 'number' &&
    typeof value?.lng === 'number' &&
    typeof value?.radiusKm === 'number';

const GeoRadiusPicker: React.FC<GeoRadiusPickerProps> = ({value, onChange}) => {
    const [center, setCenter] = useState<GeoPoint | null>(isValidGeoValue(value) ? {lat: value.lat, lng: value.lng} : null);
    const [radiusKm, setRadiusKm] = useState<number>(isValidGeoValue(value) ? value.radiusKm : defaultRadiusKm);

    useEffect(() => {
        if (isValidGeoValue(value)) {
            setCenter({lat: value.lat, lng: value.lng});
            setRadiusKm(value.radiusKm);
            return;
        }

        if (!navigator.geolocation) {
            setCenter(defaultCenter);
            onChange?.({...defaultCenter, radiusKm: defaultRadiusKm});
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const browserCenter = {
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                };

                setCenter(browserCenter);
                onChange?.({...browserCenter, radiusKm: defaultRadiusKm});
            },
            () => {
                setCenter(defaultCenter);
                onChange?.({...defaultCenter, radiusKm: defaultRadiusKm});
            }
        );
    }, [value?.lat, value?.lng, value?.radiusKm]);

    const triggerChange = (nextCenter: GeoPoint, nextRadiusKm: number) => {
        onChange?.({
            lat: nextCenter.lat,
            lng: nextCenter.lng,
            radiusKm: nextRadiusKm,
        });
    };

    const handleCenterChange = (nextCenter: GeoPoint) => {
        setCenter(nextCenter);
        triggerChange(nextCenter, radiusKm);
    };

    const handleRadiusChange = (nextRadiusKm: number | null) => {
        const safeRadiusKm = nextRadiusKm || minRadiusKm;
        setRadiusKm(safeRadiusKm);
        if (center) triggerChange(center, safeRadiusKm);
    };

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition((pos) => {
            handleCenterChange({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
            });
        });
    };

    if (!center) return <p>Loading map...</p>;

    return (
        <div className={styles.wrapper}>
            <GeoRadiusMap center={center} radiusKm={radiusKm} onCenterChange={handleCenterChange}/>
            <div className={styles.controls}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} md={14}>
                        <Slider
                            min={minRadiusKm}
                            max={maxRadiusKm}
                            step={1}
                            value={radiusKm}
                            onChange={handleRadiusChange}
                        />
                    </Col>
                    <Col xs={12} md={5}>
                        <InputNumber
                            min={minRadiusKm}
                            max={maxRadiusKm}
                            step={1}
                            value={radiusKm}
                            onChange={handleRadiusChange}
                            style={{ width: '100%' }}
                            addonAfter="km"
                        />
                    </Col>
                    <Col xs={12} md={5}>
                        <Button block icon={<AimOutlined/>} onClick={handleUseCurrentLocation}>
                            Current
                        </Button>
                    </Col>
                </Row>

                <div className={styles.coordinates}>
                    <Text type="secondary">Lat {center.lat.toFixed(5)}</Text>
                    <Text type="secondary">Lng {center.lng.toFixed(5)}</Text>
                    <Text type="secondary">Radius {radiusKm.toFixed(0)} km</Text>
                </div>
            </div>
        </div>
    );
};

export default GeoRadiusPicker;
